const API_BASE = process.env.REACT_APP_API_URL || "";
const TOKEN_KEY = "aa-auth-token";

export function getStoredToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setStoredToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export async function api(path, options = {}) {
  const { suppressUnauthorizedEvent, ...fetchOptions } = options;
  const token = getStoredToken();
  const headers = { ...fetchOptions.headers };

  if (!(fetchOptions.body instanceof FormData)) {
    if (!headers["Content-Type"]) {
      headers["Content-Type"] = "application/json";
    }
  } else {
    delete headers["Content-Type"];
  }

  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...fetchOptions, headers });
  let data = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { error: text };
    }
  }

  if (!res.ok) {
    if (
      res.status === 401 &&
      typeof window !== "undefined" &&
      !suppressUnauthorizedEvent
    ) {
      window.dispatchEvent(new CustomEvent("aa-unauthorized"));
    }

    let message = data?.error || `Request failed (${res.status})`;
    if (
      typeof message === "string" &&
      message.toLowerCase().includes("proxy")
    ) {
      message =
        "Cannot reach the API. Run npm run server (port 3015) or npm run dev, then try again.";
    }
    const err = new Error(message);
    err.status = res.status;
    if (data && typeof data === 'object') {
      Object.assign(err, data);
    }
    throw err;
  }

  return data;
}

export const authApi = {
  signUp: (body) =>
    api("/api/auth/signup", {
      method: "POST",
      body: JSON.stringify(body),
      suppressUnauthorizedEvent: true,
    }),
  signIn: (body) =>
    api("/api/auth/signin", {
      method: "POST",
      body: JSON.stringify(body),
      suppressUnauthorizedEvent: true,
    }),
  signInWithGoogle: (body) =>
    api("/api/auth/google", {
      method: "POST",
      body: JSON.stringify(body),
      suppressUnauthorizedEvent: true,
    }),
  verifyOtp: (body) =>
    api("/api/auth/verify-otp", {
      method: "POST",
      body: JSON.stringify(body),
      suppressUnauthorizedEvent: true,
    }),
  resendOtp: (body) =>
    api("/api/auth/resend-otp", {
      method: "POST",
      body: JSON.stringify(body),
      suppressUnauthorizedEvent: true,
    }),
  updateProfile: (body) =>
    api("/api/auth/profile", { method: "PATCH", body: JSON.stringify(body) }),
  me: () => api("/api/auth/me", { suppressUnauthorizedEvent: true }),
  signOut: () => api("/api/auth/signout", { method: "POST" }),
  forgotPassword: (body) =>
    api("/api/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify(body),
      suppressUnauthorizedEvent: true,
    }),
  resetPassword: (body) =>
    api("/api/auth/reset-password", {
      method: "POST",
      body: JSON.stringify(body),
      suppressUnauthorizedEvent: true,
    }),
  uploadAvatar: (body) =>
    api("/api/auth/avatar", { method: "POST", body: JSON.stringify(body) }),
  removeAvatar: () =>
    api("/api/auth/avatar", { method: "DELETE" }),
};

export const adminApi = {
  stats: () => api("/api/admin/stats"),
  analytics: (timeframe) => api(`/api/admin/analytics?timeframe=${timeframe}`),
  users: () => api("/api/admin/users"),
  toggleAdminRole: (id, is_admin) =>
    api(`/api/admin/users/${id}/role`, {
      method: "PATCH",
      body: JSON.stringify({ is_admin }),
    }),
  toggleBlockUser: (id, is_blocked) =>
    api(`/api/admin/users/${id}/block`, {
      method: "PATCH",
      body: JSON.stringify({ is_blocked }),
    }),
  deleteUser: (id) => api(`/api/admin/users/${id}`, { method: "DELETE" }),
};

export const settingsApi = {
  get: () => api("/api/settings"),
  update: (body) =>
    api("/api/settings", { method: "PUT", body: JSON.stringify(body) }),
  uploadLogo: (body) =>
    api("/api/settings/upload-logo", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateNavigation: (body) =>
    api("/api/settings/navigation", {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  createFooterColumn: (body) =>
    api("/api/settings/footer-columns", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateFooterColumns: (body) =>
    api("/api/settings/footer-columns", {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  deleteFooterColumn: (key) =>
    api(`/api/settings/footer-columns/${key}`, { method: "DELETE" }),
};

export const postsApi = {
  list: () => api("/api/posts"),
  get: (id) => api(`/api/posts/${id}`),
  create: (body) =>
    api("/api/posts", { method: "POST", body: JSON.stringify(body) }),
  update: (id, body) =>
    api(`/api/posts/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  delete: (id) => api(`/api/posts/${id}`, { method: "DELETE" }),
  addComment: (id, body) =>
    api(`/api/posts/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateComment: (postId, commentId, body) =>
    api(`/api/posts/${postId}/comments/${commentId}`, {
      method: 'PUT',
      body: JSON.stringify({ body }),
    }),
  deleteComment: (postId, commentId) =>
    api(`/api/posts/${postId}/comments/${commentId}`, {
      method: 'DELETE',
    }),
  vote: (id, body) =>
    api(`/api/posts/${id}/vote`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  markViewed: (id) =>
    api(`/api/posts/${id}/view`, {
      method: "POST",
    }),
  topContributors: () => api("/api/posts/top-contributors"),
  // Admin methods
  listAdmin: () => api("/api/posts/admin"),
  createAdmin: (body) =>
    api("/api/posts/admin", { method: "POST", body: JSON.stringify(body) }),
  updateAdmin: (id, body) =>
    api(`/api/posts/admin/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  deleteAdmin: (id) => api(`/api/posts/admin/${id}`, { method: "DELETE" }),
  updateCommentAdmin: (postId, commentId, body) =>
    api(`/api/posts/admin/${postId}/comments/${commentId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  deleteCommentAdmin: (postId, commentId) =>
    api(`/api/posts/admin/${postId}/comments/${commentId}`, {
      method: "DELETE",
    }),
};

export const toolsApi = {
  list: () => api("/api/tools"),
  create: (body) =>
    api("/api/tools/admin", { method: "POST", body: JSON.stringify(body) }),
  update: (id, body) =>
    api(`/api/tools/admin/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  delete: (id) => api(`/api/tools/admin/${id}`, { method: "DELETE" }),
  reorder: (body) =>
    api("/api/tools/admin/reorder", {
      method: "PUT",
      body: JSON.stringify(body),
    }),
};

export const eventsApi = {
  list: () => api("/api/events"),
  rsvp: (eventId, body) =>
    api(`/api/events/${eventId}/rsvp`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  submitProposal: (body) =>
    api("/api/events/proposals", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // Admin endpoints
  getRsvpsAdmin: (id) => api(`/api/events/admin/${id}/rsvps`),
  listProposals: () => api("/api/events/admin/proposals"),
  approveProposal: (id, body) =>
    api(`/api/events/admin/proposals/${id}/approve`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  rejectProposal: (id) =>
    api(`/api/events/admin/proposals/${id}`, { method: "DELETE" }),
  deleteProposal: (id) =>
    api(`/api/events/admin/proposals/${id}/force`, { method: "DELETE" }),
  create: (body) =>
    api("/api/events/admin", { method: "POST", body: JSON.stringify(body) }),
  update: (id, body) =>
    api(`/api/events/admin/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  delete: (id) => api(`/api/events/admin/${id}`, { method: "DELETE" }),
  reorder: (body) =>
    api("/api/events/admin/reorder", {
      method: "PUT",
      body: JSON.stringify(body),
    }),
};

export const articlesApi = {
  list: () => api("/api/articles"),
  listAdmin: () => api("/api/articles/admin"),
  get: (id) => api(`/api/articles/${id}`),
  getAdmin: (id) => api(`/api/articles/admin/${id}`),
  uploadCover: (body) =>
    api("/api/articles/upload-cover", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  create: (body) =>
    api("/api/articles", { method: "POST", body: JSON.stringify(body) }),
  update: (id, body) =>
    api(`/api/articles/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  togglePublish: (id, isPublished) =>
    api(`/api/articles/${id}/publish`, {
      method: "PATCH",
      body: JSON.stringify({ is_published: isPublished }),
    }),
  delete: (id) => api(`/api/articles/${id}`, { method: "DELETE" }),
  getComments: (id) => api(`/api/articles/${id}/comments`),
  addComment: (id, body) =>
    api(`/api/articles/${id}/comments`, {
      method: "POST",
      body: JSON.stringify({ body }),
    }),
  updateComment: (id, commentId, body) =>
    api(`/api/articles/${id}/comments/${commentId}`, {
      method: "PUT",
      body: JSON.stringify({ body }),
    }),
  deleteComment: (id, commentId) =>
    api(`/api/articles/${id}/comments/${commentId}`, {
      method: "DELETE",
    }),
  vote: (id, direction) =>
    api(`/api/articles/${id}/vote`, {
      method: "POST",
      body: JSON.stringify({ direction }),
    }),
};

export const blogpostsApi = {
  list: () => api("/api/blogposts"),
  listAdmin: () => api("/api/blogposts/admin"),
  get: (id) => api(`/api/blogposts/${id}`),
  getAdmin: (id) => api(`/api/blogposts/admin/${id}`),
  uploadCover: (body) =>
    api("/api/blogposts/upload-cover", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  create: (body) =>
    api("/api/blogposts", { method: "POST", body: JSON.stringify(body) }),
  update: (id, body) =>
    api(`/api/blogposts/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  togglePublish: (id, isPublished) =>
    api(`/api/blogposts/${id}/publish`, {
      method: "PATCH",
      body: JSON.stringify({ is_published: isPublished }),
    }),
  delete: (id) => api(`/api/blogposts/${id}`, { method: "DELETE" }),
  getComments: (id) => api(`/api/blogposts/${id}/comments`),
  addComment: (id, body) =>
    api(`/api/blogposts/${id}/comments`, {
      method: "POST",
      body: JSON.stringify({ body }),
    }),
  updateComment: (id, commentId, body) =>
    api(`/api/blogposts/${id}/comments/${commentId}`, {
      method: "PUT",
      body: JSON.stringify({ body }),
    }),
  deleteComment: (id, commentId) =>
    api(`/api/blogposts/${id}/comments/${commentId}`, {
      method: "DELETE",
    }),
  vote: (id, direction) =>
    api(`/api/blogposts/${id}/vote`, {
      method: "POST",
      body: JSON.stringify({ direction }),
    }),
};

export const newsApi = {
  feed: () => api("/api/news/feed"),
};

export const screenReadersApi = {
  list: () => api("/api/screen-readers"),
  get: (id) => api(`/api/screen-readers/${id}`),
  listAdmin: () => api("/api/screen-readers/admin/all"),
  getAdmin: (id) => api(`/api/screen-readers/admin/${id}`),
  create: (data) =>
    api("/api/screen-readers", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  update: (id, data) =>
    api(`/api/screen-readers/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  togglePublish: (id, isPublished) =>
    api(`/api/screen-readers/${id}/publish`, {
      method: "PATCH",
      body: JSON.stringify({ is_published: isPublished }),
    }),
  delete: (id) =>
    api(`/api/screen-readers/${id}`, {
      method: "DELETE",
    }),
};

export const resourcesApi = {
  // Public
  list: () => api("/api/resources"),
  submitProposal: (body) =>
    api("/api/resources/proposals", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Admin
  listProposals: () => api("/api/resources/admin/proposals"),
  approveProposal: (id, body) =>
    api(`/api/resources/admin/proposals/${id}/approve`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  rejectProposal: (id) =>
    api(`/api/resources/admin/proposals/${id}`, { method: "DELETE" }),
  deleteProposal: (id) =>
    api(`/api/resources/admin/proposals/${id}/force`, { method: "DELETE" }),
  reorder: (body) =>
    api("/api/resources/admin/reorder", {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  create: (body) =>
    api("/api/resources/admin", { method: "POST", body: JSON.stringify(body) }),
  update: (id, body) =>
    api(`/api/resources/admin/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  delete: (id) => api(`/api/resources/admin/${id}`, { method: "DELETE" }),
};

export const usersApi = {
  getProfile: (id) => api(`/api/users/${id}/profile`),
  getRecentlyViewed: () => api('/api/users/me/recently-viewed'),
};

export const jobsApi = {
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.query) queryParams.append('query', params.query);
    if (params.location) queryParams.append('location', params.location);
    if (params.page) queryParams.append('page', params.page);
    if (params.remote_jobs_only) queryParams.append('remote_jobs_only', params.remote_jobs_only);
    
    const queryString = queryParams.toString();
    return api(`/api/jobs${queryString ? `?${queryString}` : ''}`);
  },
};

export const gamesApi = {
  list: () => api("/api/games"),
  get: (slug) => api(`/api/games/${slug}`),
  listAdmin: () => api("/api/admin/games"),
  create: (formData) => api("/api/admin/games", { method: "POST", body: formData }),
  update: (id, formData) => api(`/api/admin/games/${id}`, { method: "PUT", body: formData }),
  delete: (id) => api(`/api/admin/games/${id}`, { method: "DELETE" }),
  toggleActive: (id, isActive) => {
    const fd = new FormData();
    fd.append("is_active", isActive);
    return api(`/api/admin/games/${id}`, { method: "PUT", body: fd });
  }
};
